import type {
  ConjointAttribute,
  ConjointDesign,
  ConjointProfile,
  ConjointTask,
  OptionId,
} from "../types";
import { shuffleArray } from "@/utils/helpers";
import seedrandom from "seedrandom";

// Distinct products the grid can express, which decides whether within-task
// duplicates are avoidable at all and doubles as an authoring guardrail.
export const computeProfileSpaceSize = (
  attributes: Pick<ConjointAttribute, "levels">[]
): number =>
  attributes.reduce(
    (acc, a) => acc * Math.max(1, a.levels.length),
    attributes.length === 0 ? 0 : 1
  );

const profileKey = (profile: ConjointProfile): string =>
  profile.levels.map((l) => l.levelId).join("|");

// The conjoint analog of generateMaxDiffDesign: balanced level exposure per
// attribute, no duplicate profiles in a task, deterministic for a given seed.
export const generateConjointDesign = ({
  attributes,
  profilesPerScreen,
  numTasks,
  seed,
}: {
  attributes: ConjointAttribute[];
  profilesPerScreen: number;
  numTasks: number;
  seed?: number;
}): ConjointDesign => {
  // Coerce to integers: a fractional count makes totalSlots fractional and
  // leaves the last profile with an undefined level.
  const taskCount = Math.floor(numTasks);
  const perScreen = Math.floor(profilesPerScreen);
  if (perScreen < 2) throw new Error("profilesPerScreen must be >= 2");
  if (taskCount < 1) throw new Error("numTasks must be >= 1");
  const usableAttributes = attributes.filter((a) => a.levels.length > 0);
  if (usableAttributes.length === 0) return { tasks: [] };

  const rng = seedrandom(
    String(seed ?? Math.floor(Math.random() * 0xffffffff))
  );

  const totalSlots = taskCount * perScreen;

  // One balanced, shuffled level sequence per attribute (length = totalSlots).
  // Independent shuffles across attributes give near-orthogonal columns.
  const columns: OptionId[][] = usableAttributes.map((attr) =>
    buildBalancedColumn(
      attr.levels.map((l) => l.id),
      totalSlots,
      rng
    )
  );

  // Assemble profiles: slot s -> profile, columns[a][s] = that attr's level.
  const buildProfileAt = (slot: number): ConjointProfile => ({
    levels: usableAttributes.map((attr, a) => ({
      attributeId: attr.id,
      levelId: columns[a][slot],
    })),
  });

  const tasks: ConjointTask[] = [];
  for (let t = 0; t < taskCount; t++) {
    const profiles: ConjointProfile[] = [];
    for (let p = 0; p < perScreen; p++) {
      profiles.push(buildProfileAt(t * perScreen + p));
    }
    tasks.push({ taskIndex: t, profiles });
  }

  // Repair within-task duplicate profiles when the space allows distinct ones.
  const canAvoidDuplicates =
    computeProfileSpaceSize(usableAttributes) >= perScreen;
  if (canAvoidDuplicates) {
    fixDuplicateProfiles(tasks, columns, usableAttributes.length, perScreen);
  }

  // Skipped when no attribute has a preference direction, since dominance is
  // undefinable without one.
  const levelRanks = buildLevelRanksByAttribute(usableAttributes);
  if (levelRanks.some((r) => r !== null) && canAvoidDuplicates) {
    fixDominatedProfiles(
      tasks,
      columns,
      usableAttributes.length,
      perScreen,
      levelRanks
    );
  }

  return { tasks };
};

// levelId -> rank, higher being more preferred; null for unordered attributes.
// The authored order is the scale: `ascending` puts the best level last.
const buildLevelRanksByAttribute = (
  attributes: ConjointAttribute[]
): Array<Map<OptionId, number> | null> =>
  attributes.map((attr) => {
    const dir = attr.preferenceDirection;
    if (!dir) return null;
    const map = new Map<OptionId, number>();
    const n = attr.levels.length;
    attr.levels.forEach((l, i) => {
      // ascending: rank = i (last = highest);
      // descending: rank = n-1-i (first = highest).
      map.set(l.id, dir === "ascending" ? i : n - 1 - i);
    });
    return map;
  });

// Build a length-`totalSlots` sequence where each level id appears as evenly as
// possible, then shuffle it. Mirrors the MaxDiff "bag" balance approach.
const buildBalancedColumn = (
  levelIds: OptionId[],
  totalSlots: number,
  rng: () => number
): OptionId[] => {
  const bag: OptionId[] = [];
  for (let i = 0; bag.length < totalSlots; i++) {
    bag.push(levelIds[i % levelIds.length]);
  }
  return shuffleArray(bag, rng);
};

// Swap one attribute cell between tasks to break a duplicate. Staying in one
// column keeps every attribute's global level counts intact.
const fixDuplicateProfiles = (
  tasks: ConjointTask[],
  columns: OptionId[][],
  numAttributes: number,
  profilesPerScreen: number
): void => {
  const slotOf = (taskIdx: number, p: number) =>
    taskIdx * profilesPerScreen + p;

  const taskHasDuplicate = (task: ConjointTask): [number, number] | null => {
    const seen = new Map<string, number>();
    for (let p = 0; p < task.profiles.length; p++) {
      const key = profileKey(task.profiles[p]);
      const prev = seen.get(key);
      if (prev !== undefined) return [prev, p];
      seen.set(key, p);
    }
    return null;
  };

  const rebuildProfile = (taskIdx: number, p: number): ConjointProfile => ({
    levels: tasks[taskIdx].profiles[p].levels.map((lvl, a) => ({
      attributeId: lvl.attributeId,
      levelId: columns[a][slotOf(taskIdx, p)],
    })),
  });

  const maxPasses = 12;
  for (let pass = 0; pass < maxPasses; pass++) {
    let fixedAny = false;
    for (let t = 0; t < tasks.length; t++) {
      let dup = taskHasDuplicate(tasks[t]);
      let guard = 0;
      while (dup && guard++ < profilesPerScreen * numAttributes) {
        const [, p2] = dup;
        const fixed = trySwapToBreakDuplicate(
          tasks,
          columns,
          numAttributes,
          profilesPerScreen,
          t,
          p2,
          slotOf,
          rebuildProfile
        );
        if (!fixed) break;
        fixedAny = true;
        dup = taskHasDuplicate(tasks[t]);
      }
    }
    if (!fixedAny) break;
  }
};

// Swap one attribute cell against another task's, such that this task has
// fewer duplicates and the other gains none. Fewer rather than none, since one
// swap cannot clear a screen whose three profiles are all the same.
const trySwapToBreakDuplicate = (
  tasks: ConjointTask[],
  columns: OptionId[][],
  numAttributes: number,
  profilesPerScreen: number,
  taskIdx: number,
  p: number,
  slotOf: (taskIdx: number, p: number) => number,
  rebuildProfile: (taskIdx: number, p: number) => ConjointProfile
): boolean => {
  const duplicateCount = (task: ConjointTask): number =>
    task.profiles.length - new Set(task.profiles.map(profileKey)).size;
  const srcBefore = duplicateCount(tasks[taskIdx]);

  for (let a = 0; a < numAttributes; a++) {
    const srcSlot = slotOf(taskIdx, p);
    const srcLevel = columns[a][srcSlot];
    for (let t2 = 0; t2 < tasks.length; t2++) {
      if (t2 === taskIdx) continue;
      const dstBefore = duplicateCount(tasks[t2]);
      for (let p2 = 0; p2 < profilesPerScreen; p2++) {
        const dstSlot = slotOf(t2, p2);
        if (columns[a][dstSlot] === srcLevel) continue;
        // swap cell a between the two slots
        columns[a][srcSlot] = columns[a][dstSlot];
        columns[a][dstSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
        if (
          duplicateCount(tasks[taskIdx]) < srcBefore &&
          duplicateCount(tasks[t2]) <= dstBefore
        ) {
          return true;
        }
        // revert
        columns[a][dstSlot] = columns[a][srcSlot];
        columns[a][srcSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
      }
    }
  }
  return false;
};

// B dominates A iff every ordered attribute has rank(B) >= rank(A) with one
// strict, and every unordered one matches, since Brand A vs B is a tie.
const findDominatedPair = (
  task: ConjointTask,
  levelRanks: Array<Map<OptionId, number> | null>
): [number, number] | null => {
  const profiles = task.profiles;
  for (let i = 0; i < profiles.length; i++) {
    for (let j = 0; j < profiles.length; j++) {
      if (i === j) continue;
      let strict = false;
      let dominated = true;
      for (let a = 0; a < profiles[i].levels.length; a++) {
        const li = profiles[i].levels[a].levelId;
        const lj = profiles[j].levels[a].levelId;
        const ranks = levelRanks[a];
        if (!ranks) {
          // Unordered attribute: any difference breaks dominance.
          if (li !== lj) {
            dominated = false;
            break;
          }
          continue;
        }
        const ri = ranks.get(li) ?? 0;
        const rj = ranks.get(lj) ?? 0;
        if (ri < rj) {
          dominated = false;
          break;
        }
        if (ri > rj) strict = true;
      }
      if (dominated && strict) return [i, j]; // i dominates j
    }
  }
  return null;
};

// Reuses the duplicate fixer's column-preserving swap so level balance survives.
// Stops at the first swap leaving both touched tasks clean.
const fixDominatedProfiles = (
  tasks: ConjointTask[],
  columns: OptionId[][],
  numAttributes: number,
  profilesPerScreen: number,
  levelRanks: Array<Map<OptionId, number> | null>
): void => {
  const slotOf = (taskIdx: number, p: number) =>
    taskIdx * profilesPerScreen + p;

  const rebuildProfile = (taskIdx: number, p: number): ConjointProfile => ({
    levels: tasks[taskIdx].profiles[p].levels.map((lvl, a) => ({
      attributeId: lvl.attributeId,
      levelId: columns[a][slotOf(taskIdx, p)],
    })),
  });

  const noDup = (task: ConjointTask): boolean => {
    const keys = task.profiles.map(profileKey);
    return new Set(keys).size === keys.length;
  };

  const countTaskDominatedPairs = (task: ConjointTask): number => {
    let n = 0;
    for (let i = 0; i < task.profiles.length; i++) {
      for (let j = 0; j < task.profiles.length; j++) {
        if (i === j) continue;
        // findDominatedPair only returns the first; reuse its scan-shape via
        // a single-pair check by setting a fake task with just (i, j).
        const probe: ConjointTask = {
          taskIndex: task.taskIndex,
          profiles: [task.profiles[i], task.profiles[j]],
        };
        if (findDominatedPair(probe, levelRanks) !== null) n += 1;
      }
    }
    return n;
  };

  const isClean = (task: ConjointTask): boolean =>
    noDup(task) && findDominatedPair(task, levelRanks) === null;

  // Strict pass first: only accept swaps that leave both touched tasks fully
  // clean. This finishes most designs.
  const maxStrictPasses = 12;
  for (let pass = 0; pass < maxStrictPasses; pass++) {
    let fixedAny = false;
    for (let t = 0; t < tasks.length; t++) {
      let pair = findDominatedPair(tasks[t], levelRanks);
      let guard = 0;
      while (pair && guard++ < profilesPerScreen * numAttributes) {
        const [, dominatedP] = pair;
        const fixed = trySwapToBreakDominance(
          tasks,
          columns,
          numAttributes,
          profilesPerScreen,
          t,
          dominatedP,
          slotOf,
          rebuildProfile,
          isClean
        );
        if (!fixed) break;
        fixedAny = true;
        pair = findDominatedPair(tasks[t], levelRanks);
      }
    }
    if (!fixedAny) break;
  }

  // Some tasks are structurally uncleanable, so fall back to strictly reducing
  // dominated pairs, which is monotone and therefore terminates. Every pass
  // removes a pair, so the most pairs a design can hold bounds the passes.
  const maxRelaxedPasses =
    tasks.length * profilesPerScreen * (profilesPerScreen - 1);
  for (let pass = 0; pass < maxRelaxedPasses; pass++) {
    let totalBefore = 0;
    for (const task of tasks) totalBefore += countTaskDominatedPairs(task);
    if (totalBefore === 0) break;

    let improved = false;
    for (let t = 0; t < tasks.length; t++) {
      if (findDominatedPair(tasks[t], levelRanks) === null) continue;
      // Try every dominated profile in this task as the swap source.
      for (let p = 0; p < tasks[t].profiles.length && !improved; p++) {
        if (
          tryRelaxedSwap(
            tasks,
            columns,
            numAttributes,
            profilesPerScreen,
            t,
            p,
            slotOf,
            rebuildProfile,
            noDup,
            countTaskDominatedPairs
          )
        ) {
          improved = true;
        }
      }
      if (improved) break; // re-scan totals from the top of the pass.
    }
    if (!improved) break; // no swap reduces the count -> nothing more to do.
  }
};

// Fallback when no swap leaves both tasks perfectly clean: accept any that
// strictly lowers the combined dominated-pair count without adding a duplicate.
const tryRelaxedSwap = (
  tasks: ConjointTask[],
  columns: OptionId[][],
  numAttributes: number,
  profilesPerScreen: number,
  taskIdx: number,
  p: number,
  slotOf: (taskIdx: number, p: number) => number,
  rebuildProfile: (taskIdx: number, p: number) => ConjointProfile,
  noDup: (task: ConjointTask) => boolean,
  countTaskDominatedPairs: (task: ConjointTask) => number
): boolean => {
  const before = countTaskDominatedPairs(tasks[taskIdx]);
  for (let a = 0; a < numAttributes; a++) {
    const srcSlot = slotOf(taskIdx, p);
    const srcLevel = columns[a][srcSlot];
    for (let t2 = 0; t2 < tasks.length; t2++) {
      const beforeT2 = t2 === taskIdx ? 0 : countTaskDominatedPairs(tasks[t2]);
      for (let p2 = 0; p2 < profilesPerScreen; p2++) {
        if (t2 === taskIdx && p2 === p) continue;
        const dstSlot = slotOf(t2, p2);
        if (columns[a][dstSlot] === srcLevel) continue;
        columns[a][srcSlot] = columns[a][dstSlot];
        columns[a][dstSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
        const afterT = countTaskDominatedPairs(tasks[taskIdx]);
        const afterT2 = t2 === taskIdx ? 0 : countTaskDominatedPairs(tasks[t2]);
        const lowered =
          t2 === taskIdx
            ? afterT < before
            : afterT + afterT2 < before + beforeT2;
        if (lowered && noDup(tasks[taskIdx]) && noDup(tasks[t2])) {
          return true;
        }
        // revert
        columns[a][dstSlot] = columns[a][srcSlot];
        columns[a][srcSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
      }
    }
  }
  return false;
};

// Column-preserving swaps against every other task's same-attribute cell until
// both touched tasks are free of dominance and duplicates.
const trySwapToBreakDominance = (
  tasks: ConjointTask[],
  columns: OptionId[][],
  numAttributes: number,
  profilesPerScreen: number,
  taskIdx: number,
  p: number,
  slotOf: (taskIdx: number, p: number) => number,
  rebuildProfile: (taskIdx: number, p: number) => ConjointProfile,
  isClean: (task: ConjointTask) => boolean
): boolean => {
  // Same-task swaps are allowed here, unlike in the duplicate fixer: column
  // totals are unchanged either way, and they clear chains cross-task misses.
  for (let a = 0; a < numAttributes; a++) {
    const srcSlot = slotOf(taskIdx, p);
    const srcLevel = columns[a][srcSlot];
    for (let t2 = 0; t2 < tasks.length; t2++) {
      for (let p2 = 0; p2 < profilesPerScreen; p2++) {
        if (t2 === taskIdx && p2 === p) continue;
        const dstSlot = slotOf(t2, p2);
        if (columns[a][dstSlot] === srcLevel) continue;
        columns[a][srcSlot] = columns[a][dstSlot];
        columns[a][dstSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
        if (isClean(tasks[taskIdx]) && isClean(tasks[t2])) return true;
        // revert
        columns[a][dstSlot] = columns[a][srcSlot];
        columns[a][srcSlot] = srcLevel;
        tasks[taskIdx].profiles[p] = rebuildProfile(taskIdx, p);
        tasks[t2].profiles[p2] = rebuildProfile(t2, p2);
      }
    }
  }
  return false;
};
